<template>
  <error-boundary module="view-module">
    <hr-module class="view-module" no-header mobile-header>
      <hr-module-header
        class="hr-module-header__title col-xs-12 col-sm-10 col-centered"
        :title="t('view-module:title')"
      >
        <p class="hr-module-header__description">
          {{ t('view-module:description') }}
        </p>
      </hr-module-header>
      <p v-if="state.error" class="alert alert-danger">{{ state.error }}</p>
      <h-r-module-section>
        <div
          v-for="(document, index) in state.documents"
          :key="index"
          class="view-module__DSCard"
        >
          <div class="view-module__DSCard__header">
            <div class="view-module__DSCard__header__title">
              <strong>{{ document.title }}</strong>
            </div>
            <div
              data-test-id="edit-button"
              class="view-module__DSCard__header__edit"
              @click="edit(document)"
            >
              Edit
            </div>
          </div>
          <template v-if="document.name === DOCUMENT_NAMES.SUPER_API">
            <hr-super-fields-view :fields="document.fields" />
          </template>
          <template v-else>
            <hr-field-view
              v-for="(field, index) in document.fields"
              :key="`field-${index}`"
              :value="field"
              owner
            />
          </template>
        </div>
      </h-r-module-section>

      <template v-slot:footer>
        <hr-footer :button="t('view-module:submit')" @click="submit">
          {{ t('view-module:submit-bar') }}
        </hr-footer>
      </template>
    </hr-module>
  </error-boundary>
</template>

<script>
import HrModule from '@/components/hr-module.vue';
import HrFieldView from '@/components/hr-field-view.vue';
import HrFooter from '@/components/hr-footer.vue';
import HRModuleSection from '@/components/HRModuleSection.vue';
import HrModuleHeader from '@/components/hr-module-header.vue';
import {
  submit as onboardingApiSubmit,
  status as onboardingApiStatus,
} from '@/core/svc-client/onboarding';
import { reactive } from 'vue';
import store from '@/store';
import router from '@/router';
import t from '@/lib/i18next';
import cloneObj from '@/core/cloneObj';
import {
  filterSuperFieldsToBeIncluded,
  filterSuperOptionalFieldsToBeIncluded,
} from '@/store/composables/au/superannuation-rule';
import { filterTFNFieldsToBeIncluded } from '@/store/composables/au/tfn-declaration-rule';
import { filterWorkRightsToBeIncluded } from '@/store/composables/document-completion';
import { DOCUMENT_NAMES, PERSONAL_DETAILS } from '@/core/field-names';
import { HR_ONBOARDING_STATES } from '../core/onboarding-states';
import { logWebHrError } from '@/core/logger/log-wrapper';
import ErrorBoundary from '@/components/error-boundary.vue';
import { mapTfnAndSuper } from '@/core/documents/au/superstep-mappers';
import HrSuperFieldsView from '@/components/au/hr-super-fields-view.vue';
import { useOnboardingReviewAnalytics } from '@/composables/use-analytics';

export default {
  name: 'view-module',
  components: {
    ErrorBoundary,
    HrModule,
    HrFooter,
    HRModuleSection,
    HrModuleHeader,
    HrFieldView,
    HrSuperFieldsView,
  },
  setup() {
    const stateModule = store.state;
    const state = reactive({
      documents: [],
      error: '',
    });

    const { trackSubmitRequested, trackSubmitSuccess, trackSubmitFailure } =
      useOnboardingReviewAnalytics({
        installationId: stateModule.onboarding?.company?.id,
        getDocuments: () => state.documents,
      });

    function documentsToPreview(documents = []) {
      const newDocuments = documents.map((document) => {
        switch (document.name) {
          case 'superannuation':
            return filterWithNameBased(document);
          case 'tfnDeclaration':
            return filterWithNameBased(document);
          case 'workRights':
            return filterWithNameBased(document);
          case DOCUMENT_NAMES.SUPER_API:
            return convertSuperapiDocument(document);
        }
        return document;
      });
      return newDocuments;
    }

    const fieldFilterFunctions = {
      [DOCUMENT_NAMES.SUPERANNUATION]: filterSuperFieldsToBeIncluded,
      [DOCUMENT_NAMES.TFN_DECLARATION]: filterTFNFieldsToBeIncluded,
      [DOCUMENT_NAMES.WORK_RIGHTS]: filterWorkRightsToBeIncluded,
    };

    const optionalFieldFilterFunctions = {
      [DOCUMENT_NAMES.SUPERANNUATION]: filterSuperOptionalFieldsToBeIncluded,
    };

    function convertSuperapiDocument(document) {
      if (store.getters.isSuperAPIEnabled) {
        const personalDetailsDoc = stateModule.onboarding?.documents?.find(
          (d) => d.name === DOCUMENT_NAMES.PERSONAL_DETAILS
        );
        const employeeDob = personalDetailsDoc?.fields?.find(
          (f) => f.name === PERSONAL_DETAILS.DOB
        )?.stringValue;

        const mappedData = mapTfnAndSuper(
          store.state.superApiOnboardingSession,
          employeeDob
        );

        return {
          ...document,
          title: document.title,
          name: document.name,
          fields: mappedData,
        };
      }
      return document;
    }

    function filterWithNameBased(document) {
      const fieldNameValueMap = document.fields.reduce((prev, field) => {
        return {
          ...prev,
          [field.name]: {
            value: field.value,
            required: field?.validation?.required,
          },
        };
      }, {});

      const fieldsToBeIncluded =
        fieldFilterFunctions[document.name](fieldNameValueMap);

      const optionalFieldsToBeIncluded = optionalFieldFilterFunctions[
        document.name
      ]
        ? optionalFieldFilterFunctions[document.name](fieldNameValueMap)
        : [];

      const fields = document.fields.reduce((fields, current) => {
        if (
          !fieldsToBeIncluded.includes(current.name) &&
          !optionalFieldsToBeIncluded.includes(current.name)
        ) {
          current.hide = true;
        }
        return [...fields, current];
      }, []);

      document.fields = [...fields];

      return document;
    }

    async function submit() {
      try {
        trackSubmitRequested();

        await onboardingApiSubmit(stateModule.onboardingId);
        store.commit('setOnboardingStatus', HR_ONBOARDING_STATES.SUBMITTED);
        router.push({ name: 'last-module' });

        trackSubmitSuccess();
      } catch (e) {
        logWebHrError({
          message: 'Failed to set submitted status',
          source: 'view-module',
          error: e,
        });
        state.error = e;

        trackSubmitFailure();
      }
    }

    function edit(document) {
      if (document.name === DOCUMENT_NAMES.SUPER_API) {
        store.commit('setNeedsSuperStepSessionReset', true);
      }
      store.commit('menuToggle', false);
      onboardingApiStatus(stateModule.onboardingId, 'STARTED');
      store.commit('setOnboardingStatus', HR_ONBOARDING_STATES.STARTED);

      router.push({
        name: 'document-module',
        params: {
          onboardingId: stateModule.onboardingId,
          document: JSON.stringify(document),
          slug: document.slug,
        },
      });
    }

    async function initialize() {
      if (store.getters.isSuperAPIEnabled) {
        await store.dispatch(
          'loadSuperApiData',
          store.state.onboarding.superStepOnboardingSessionId
        );
      }

      // prevent to go forward if document is not complete
      if (!store.getters.isDocumentsComplete) {
        router.push({
          name: 'OnboardingModule',
          params: { onboardingId: stateModule.onboardingId },
        });

        return;
      }
      state.documents = documentsToPreview(cloneObj(stateModule.documents));
    }

    initialize();

    return {
      t,
      state,
      stateModule,
      submit,
      edit,
      DOCUMENT_NAMES,
    };
  },
};
</script>

<style scoped lang="scss">
@import '~@/core/style-vars.scss';

.view-module .hr-module-header {
  margin: 0 auto 64px auto;

  @media only screen and (max-width: 768px) {
    margin: 0px auto 120px auto;

    padding: 0 20px;
  }
}

.hr-module-header__title {
  color: var(--dpt-text-color);
  text-align: left;
}

.hr-module-header__description {
  color: var(--dpt-text-color);
}

.view-module__DSCards {
  margin-bottom: 100px;
}

.view-module__DSCard {
  padding-bottom: 16px;
}

.view-module__DSCard__header {
  display: flex;
  align-items: baseline;
}

.view-module__DSCard__header__title strong {
  color: var(--dpt-text-color);
  margin-left: 5px;
  font-weight: 600;
}

.view-module__DSCard__header__title {
  flex-grow: 1;
  text-align: left;
  font-size: 1.25rem;
  margin-bottom: 12px;
  margin-top: 24px;
}

.view-module__DSCard__header__edit {
  cursor: pointer;
  color: var(--dpt-icon-color-accent);
}

.view-module__DSCard__header__edit.--disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.view-module__DSCard__header__edit .__spinner-icon {
  animation: rotate2 1s linear infinite;
}
</style>
<style>
.view-module .HRModule__body {
  padding-bottom: 90px;
}

.DSCard__header {
  border-color: var(--dpt-border-color) !important;
}

@media print {
  .view-module .HRModule__body {
    padding-bottom: 0 !important;
  }

  .view-module__DSCard {
    page-break-inside: avoid;
    margin-bottom: 20px;
  }

  .view-module__DSCard__header__edit {
    display: none !important;
  }
}
</style>
